'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

export default function Breadcrumbs() {
  const pathname = usePathname()
  const [catalogoCargado, setCatalogoCargado] = useState<{
    id: string
    nombre: string | null
  } | null>(null)
  const [reporteCargado, setReporteCargado] = useState<{
    id: string
    nombre: string | null
  } | null>(null)
  const pathSegments = pathname.split('/').filter(Boolean)
  const idCatalogo = pathname.match(/^\/gestion\/catalogos\/editar\/([^/]+)$/)?.[1]
  const idReporte = pathname.match(/^\/gestion\/analitica\/reportes\/([^/]+)$/)?.[1]

  useEffect(() => {
    if (!idCatalogo) return
    const catalogoId = idCatalogo

    const controlador = new AbortController()

    async function cargarNombreCatalogo() {
      try {
        const respuesta = await fetch(
          `/api/backend/admin/catalogos/${encodeURIComponent(catalogoId)}`,
          { signal: controlador.signal, cache: 'no-store' },
        )

        if (!respuesta.ok) {
          console.error(`Breadcrumbs: no se pudo cargar el catálogo (${respuesta.status}).`)
          setCatalogoCargado({ id: catalogoId, nombre: null })
          return
        }

        const datos = await respuesta.json() as { nombre?: unknown; name?: unknown }
        const nombre = typeof datos.nombre === 'string'
          ? datos.nombre
          : typeof datos.name === 'string'
            ? datos.name
            : null
        setCatalogoCargado({ id: catalogoId, nombre })
      } catch (error) {
        if (!controlador.signal.aborted) {
          console.error('Breadcrumbs: error al cargar el nombre del catálogo.', error)
          setCatalogoCargado({ id: catalogoId, nombre: null })
        }
      }
    }

    void cargarNombreCatalogo()
    return () => controlador.abort()
  }, [idCatalogo])

  useEffect(() => {
    if (!idReporte) return
    const reporteId = idReporte
    const controlador = new AbortController()

    async function cargarNombreReporte() {
      try {
        const respuesta = await fetch(
          `/api/backend/admin/analitica/reportes/${encodeURIComponent(reporteId)}`,
          { signal: controlador.signal, cache: 'no-store' },
        )

        if (!respuesta.ok) {
          console.error(`Breadcrumbs: no se pudo cargar el reporte (${respuesta.status}).`)
          setReporteCargado({ id: reporteId, nombre: null })
          return
        }

        const datos = await respuesta.json() as { nombre?: unknown }
        const nombre = typeof datos.nombre === 'string' && datos.nombre.trim()
          ? datos.nombre
          : null
        setReporteCargado({ id: reporteId, nombre })
      } catch (error) {
        if (!controlador.signal.aborted) {
          console.error('Breadcrumbs: error al cargar el nombre del reporte.', error)
          setReporteCargado({ id: reporteId, nombre: null })
        }
      }
    }

    void cargarNombreReporte()
    return () => controlador.abort()
  }, [idReporte])

  if (pathSegments.length <= 1) {
    return null
  }

  return (
    <div className="border-bottom bg-light-subtle px-4 py-2">
      <nav aria-label="Migas de pan">
        <ol className="m-0 flex list-none items-center gap-2 p-0 text-sm">
          <li>
            {pathSegments.length <= 1 ? (
              <span aria-current="page" className="text-gray-600">Inicio</span>
            ) : (
              <Link href="/gestion" className="text-blue-700 hover:underline">
                Inicio
              </Link>
            )}
          </li>

          {pathSegments.slice(1).map((segment, index) => {
            const allSegments = pathSegments.slice(0, index + 2)
            const href = `/${allSegments.join('/')}`
            const isLast = index === pathSegments.length - 2
            const esIdDeCatalogo = isLast && idCatalogo === segment
            const esIdDeReporte = isLast && idReporte === segment
            const nombre = esIdDeCatalogo
              ? catalogoCargado?.id === segment
                ? catalogoCargado.nombre || 'Catálogo'
                : 'Catálogo'
              : esIdDeReporte
                ? reporteCargado?.id === segment
                  ? reporteCargado.nombre || 'Reporte'
                  : 'Reporte'
                : segment.charAt(0).toUpperCase() + segment.slice(1)

            return (
              <li key={href} className="flex items-center gap-2 before:content-['/'] before:text-gray-400">
                {isLast ? (
                  <span aria-current="page" className="text-gray-600">{nombre}</span>
                ) : (
                  <Link href={href} className="text-blue-700 hover:underline">
                    {nombre}
                  </Link>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </div>
  )
}
